import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Table, 
  LayoutGrid, 
  Plus, 
  Edit, 
  Trash2
} from 'lucide-react';
import TableEditDialog from '../../components/dialogs/TableEditDialog';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import AdvancedFloorPlanEditor from '../../components/floorplan/AdvancedFloorPlanEditor';
import tableApi from '../../services/tableApi';
import type { RestaurantTable, RestaurantTableRequest } from '../../types/reservations';
import { useTableStatusSync } from '../../hooks/useTableStatusSync';

// Define TableData interface to match TableEditDialog expectations
interface TableData {
  id: string;
  name: string;
  seats: number;
  status: 'Available' | 'Occupied' | 'Reserved' | 'Maintenance';
  location: string;
  description: string;
  shape: 'circle' | 'rectangle';
  color: string;
  // Required positioning and sizing fields for backend compatibility
  xposition: number;
  yposition: number;
  width: number;
  height: number;
  rotation: number;
  floor: string;
}
import toast from 'react-hot-toast';

type SubTabType = 'tables' | 'floorplan';

export default function TableManagement() {
  const [activeSubTab, setActiveSubTab] = useState<SubTabType>('floorplan');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState<RestaurantTable | null>(null);
  const [tableToDelete, setTableToDelete] = useState<RestaurantTable | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Use the shared table status sync hook - DISABLED auto-refresh to prevent interference
  const { tables, refreshTableStatuses } = useTableStatusSync(0); // Auto-refresh disabled

  const handleDeleteTable = (table: RestaurantTable) => {
    setTableToDelete(table);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTable = async () => {
    if (!tableToDelete) return;
    
    setIsDeleting(true);
    try {
      await tableApi.deleteTable(tableToDelete.id);
      toast.success('Table deleted successfully');
      refreshTableStatuses();
      setDeleteDialogOpen(false);
      setTableToDelete(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete table';
      
      // Check if it's a foreign key constraint error
      if (errorMessage.includes('foreign key constraint') || 
          errorMessage.includes('reservation') ||
          errorMessage.includes('FKhp3dmu1l1wypc09bwb8y9qtyk') ||
          errorMessage.toLowerCase().includes('constraint')) {
        toast.error(
          'Cannot delete table: It has active or historical reservations.\n\n' +
          'To delete this table:\n' +
          '• Cancel or complete all reservations\n' +
          '• Remove from any table merges\n' +
          '• Then try deletion again',
          { duration: 6000 }
        );
      } else {
        toast.error('Failed to delete table');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const subTabs = [
    {
      id: 'floorplan' as SubTabType,
      label: 'Floor Plan',
      icon: LayoutGrid
    },
    {
      id: 'tables' as SubTabType,
      label: 'Table List',
      icon: Table
    }
    
  ];

  const getStatusColor = (status: string) => {
    // Use the shared status color utility function
    return status === 'Available' 
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      : status === 'Occupied'
      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
      : status === 'Reserved'
      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
  };

  const handleEditTable = (table: RestaurantTable) => {
    setSelectedTable(table);
    setEditDialogOpen(true);
  };

  const handleAddTable = () => {
    setSelectedTable(null);
    setEditDialogOpen(true);
  };

  // Helper functions
  const getBorderRadius = (shape?: string) => {
    switch (shape) {
      case 'circle': return '50%';
      case 'square': return '2px';
      default: return '4px';
    }
  };

  // Convert RestaurantTable to TableData for the dialog
  const convertToTableData = (table: RestaurantTable | null): TableData | null => {
    if (!table) return null;
    return {
      id: table.id.toString(),
      name: table.name,
      seats: table.seats,
      status: table.status as 'Available' | 'Occupied' | 'Reserved' | 'Maintenance',
      location: table.location,
      description: table.description || '',
      shape: (table.shape === 'circle' ? 'circle' : 'rectangle') as 'circle' | 'rectangle',
      color: table.color || '#ffffff',
      xposition: table.xposition || 0,
      yposition: table.yposition || 0,
      width: table.width || 100,
      height: table.height || 100,
      rotation: table.rotation || 0,
      floor: table.floor || table.floorId?.toString() || '1',
    };
  };

  const handleSaveTable = async (tableData: TableData) => {
    try {
      if (selectedTable) {
        // Update existing table
        const updatedTable: RestaurantTable = {
          ...selectedTable,
          name: tableData.name,
          seats: tableData.seats,
          status: tableData.status,
          location: tableData.location,
          description: tableData.description || '',
          shape: tableData.shape || 'rectangle',
          color: tableData.color || '#ffffff',
        };
        await tableApi.updateTable(updatedTable);
      } else {
        // Add new table - need to determine floor
        const tableRequest: RestaurantTableRequest = {
          name: tableData.name,
          seats: tableData.seats,
          status: tableData.status,
          location: tableData.location,
          description: tableData.description || '',
          shape: tableData.shape || 'rectangle',
          color: tableData.color || '#ffffff',
          floorId: 1, // Default floor ID, should be configurable based on selected floor
          xposition: 0,
          yposition: 0,
          width: 100,
          height: 100,
          rotation: 0,
        };
        await tableApi.createTable(tableRequest);
      }
      // Refresh tables after save
      refreshTableStatuses();
      setEditDialogOpen(false);
      toast.success(selectedTable ? 'Table updated successfully' : 'Table created successfully');
    } catch (error) {
      toast.error('Failed to save table');
    }
  };

  return (
    <div className="space-y-6">

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b ">
        {subTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeSubTab === tab.id;
          
          return (
            <Button
              key={tab.id}
              variant={isActive ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex items-center gap-2 ${
                isActive 
                  ? 'border-b-2 border-primary text-blue-600 !bg-blue-100 outline-blue-600' 
                  : 'text-muted-foreground hover:bg-[#04070628]'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </Button>
          );
        })}
      </div>

      {/* Content based on active tab */}

      <div 
        className={`h-[700px] border rounded-lg overflow-hidden bg-white dark:bg-slate-800 ${
          activeSubTab === 'floorplan' ? 'block' : 'hidden'
        } w-lg -ml-4 mr-0`}
      >
        <AdvancedFloorPlanEditor />
      </div>

      {activeSubTab === 'tables' && (
        <div className="space-y-4">
          {/* Add Table Button */}
          <div className="flex justify-end text-blue-600 !bg-white outline-blue-600">
            <Button 
            variant={'outline'}
            onClick={handleAddTable}>
              <Plus className="h-4 w-4 mr-2" />
              Add New Table
            </Button>
          </div>

          {/* Tables Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tables.map((table) => (
              <Card key={table.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{table.name}</CardTitle>
                      <CardDescription className="text-sm text-muted-foreground">
                        {table.id} • {table.floorId ? 
                          (table.floorId === 1 ? '1st Floor' : table.floorId === 2 ? '2nd Floor' : table.floorId === 3 ? 'Rooftop' : `Floor ${table.floorId}`) 
                          : table.floor || 'Unknown'}
                      </CardDescription>
                    </div>
                    <div className="flex space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEditTable(table)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTable(table)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Seats</span>
                      <span className="text-sm">{table.seats} seats</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Status</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(table.status)}`}>
                        {table.status}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Shape</span>
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-4 h-4 border"
                          style={{
                            backgroundColor: table.color,
                            borderRadius: getBorderRadius(table.shape)
                          }}
                        ></div>
                        <span className="text-sm capitalize">{table.shape}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      

      {/* Edit Dialog */}
      <TableEditDialog
        isOpen={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSaveTable}
        table={convertToTableData(selectedTable)}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTableToDelete(null);
        }}
        onConfirm={confirmDeleteTable}
        title="Delete Table"
        itemName={tableToDelete?.name || ''}
        itemType={`Table • ${tableToDelete?.seats || 0} seats • ${tableToDelete?.floorId ? 
          (tableToDelete.floorId === 1 ? '1st Floor' : tableToDelete.floorId === 2 ? '2nd Floor' : tableToDelete.floorId === 3 ? 'Rooftop' : `Floor${tableToDelete.floorId}`) 
          : tableToDelete?.floor || 'Unknown floor'}`}
        warnings={[
          'This table has active or historical reservations',
          'Table is part of a merge group',
          'Historical booking data will become orphaned'
        ]}
        isLoading={isDeleting}
      />
    </div>
  );
}
